import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { format } from 'date-fns';
import { ArrowLeft } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  PULL_LOOKBACK_MAX_MONTHS,
  PULL_LOOKBACK_MIN_MONTHS,
} from 'shared/constants/quality-metric.constants';
import type {
  QualityMetricRunPayload,
  QualityMetricRunView,
} from '@/api/quality-metric.api.private';
import {
  DEFAULT_SCHEDULE,
  MAX_MONTH_DAY,
  buildCron,
  describeCron,
  isCronLike,
  parseCron,
  toApiDate,
  toInputDate,
} from './constants';

const WEEKDAY_OPTIONS = [
  { value: '0', label: 'Sunday' },
  { value: '1', label: 'Monday' },
  { value: '2', label: 'Tuesday' },
  { value: '3', label: 'Wednesday' },
  { value: '4', label: 'Thursday' },
  { value: '5', label: 'Friday' },
  { value: '6', label: 'Saturday' },
];

const MONTH_DAY_OPTIONS = Array.from({ length: MAX_MONTH_DAY }, (_, index) => String(index + 1));

const SCHEMA = z
  .object({
    preset: z.enum(['weekly', 'monthly', 'custom']),
    weekday: z.string(),
    day: z.string(),
    time: z.string().regex(/^\d{2}:\d{2}$/, { message: 'Pick a time of day.' }),
    expression: z.string(),
    windowMode: z.enum(['lookback', 'startDate']),
    lookbackMonths: z.string(),
    startDate: z.string(),
    enabled: z.boolean(),
  })
  .superRefine((values, ctx) => {
    const issue = (path: string, message: string) =>
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: [path], message });

    if (values.preset === 'custom' && !isCronLike(values.expression)) {
      issue('expression', 'Five space-separated cron fields, for example "0 0 * * 4".');
    }

    if (values.windowMode === 'lookback') {
      const months = Number(values.lookbackMonths);
      if (!Number.isInteger(months) || months < PULL_LOOKBACK_MIN_MONTHS) {
        issue('lookbackMonths', `A whole number of months, at least ${PULL_LOOKBACK_MIN_MONTHS}.`);
      } else if (months > PULL_LOOKBACK_MAX_MONTHS) {
        issue('lookbackMonths', `The API accepts at most ${PULL_LOOKBACK_MAX_MONTHS} months.`);
      }
      return;
    }

    // An out-of-range day rolls over instead of failing — 2025-02-31 parses as
    // March 3 — so formatting it back and comparing is what actually rejects it.
    const parsed = new Date(`${values.startDate}T00:00:00`);
    const isRealDate =
      !Number.isNaN(parsed.getTime()) && format(parsed, 'yyyy-MM-dd') === values.startDate;
    if (!values.startDate || !isRealDate) {
      issue('startDate', 'Pick the date to pull from.');
    }
  });

type RunFormValues = z.infer<typeof SCHEMA>;

const toFormValues = (run: QualityMetricRunView | null): RunFormValues => {
  const schedule = run ? parseCron(run.schedule) : DEFAULT_SCHEDULE;
  return {
    ...schedule,
    windowMode: run?.startDate ? 'startDate' : 'lookback',
    lookbackMonths: run?.lookback ? run.lookback.replace(/mo$/i, '') : '3',
    startDate: run?.startDate ? toInputDate(run.startDate) : '',
    enabled: run?.enabled ?? true,
  };
};

/**
 * `PUT` is a full replace, so the payload always carries the schedule, the
 * enabled flag and exactly one of the two window fields — sending both, or
 * neither, is a `400`.
 */
const toPayload = (values: RunFormValues): QualityMetricRunPayload => ({
  schedule: buildCron(values),
  enabled: values.enabled,
  ...(values.windowMode === 'lookback'
    ? { lookback: `${Number(values.lookbackMonths)}mo` }
    : { startDate: toApiDate(values.startDate) }),
});

const Section = ({
  title,
  hint,
  children,
}: {
  title: string;
  hint?: string;
  children: React.ReactNode;
}) => (
  <section className="space-y-4 rounded-lg border p-4">
    <div>
      <h4 className="text-sm font-bold">{title}</h4>
      {hint && <p className="text-muted-foreground text-xs">{hint}</p>}
    </div>
    {children}
  </section>
);

/**
 * Create/edit view for one scheduled run: when it ticks, and how far back it
 * pulls each time.
 *
 * @param props.run - The run to edit, or `null` to add one.
 * @param props.timeZone - The zone the API evaluates schedules in, shown so
 *   nobody reads the time as their own.
 * @param props.onSave - Performs the write. Resolves to an error message to
 *   render here, or `null` once the run is stored.
 * @param props.onCancel - Leaves the editor without writing.
 * @returns The run form.
 * @example
 * <RunEditor run={editingRun} timeZone={runsState.timeZone}
 *   onSave={saveRunAndReturn} onCancel={() => setView('main')} />
 */
export default function RunEditor({
  run,
  timeZone,
  onSave,
  onCancel,
}: {
  run: QualityMetricRunView | null;
  timeZone: string;
  onSave: (payload: QualityMetricRunPayload) => Promise<string | null>;
  onCancel: () => void;
}) {
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const form = useForm<RunFormValues>({
    resolver: zodResolver(SCHEMA),
    defaultValues: toFormValues(run),
  });

  const [preset, windowMode] = form.watch(['preset', 'windowMode']);
  const preview = describeCron(buildCron(form.watch()));

  const onSubmit = async (values: RunFormValues) => {
    setSaving(true);
    setSaveError(null);
    const message = await onSave(toPayload(values));
    setSaving(false);
    if (message) setSaveError(message);
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-1 flex-col overflow-hidden">
        <div className="flex-1 space-y-4 overflow-y-auto p-6 pt-0">
          <Button type="button" variant="ghost" size="sm" className="-ml-2" onClick={onCancel}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to ingestion
          </Button>

          <Section
            title="When it runs"
            hint={`Times are in the API's time zone (${timeZone}), not yours.`}>
            <FormField
              control={form.control}
              name="preset"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Frequency</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="weekly">Weekly</SelectItem>
                      <SelectItem value="monthly">Monthly</SelectItem>
                      <SelectItem value="custom">Custom cron</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            {preset === 'weekly' && (
              <FormField
                control={form.control}
                name="weekday"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Day of the week</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {WEEKDAY_OPTIONS.map(option => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            {preset === 'monthly' && (
              <FormField
                control={form.control}
                name="day"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Day of the month</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {MONTH_DAY_OPTIONS.map(day => (
                          <SelectItem key={day} value={day}>
                            {day}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormDescription>
                      Capped at {MAX_MONTH_DAY} so no month is ever skipped.
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            {preset === 'custom' ? (
              <FormField
                control={form.control}
                name="expression"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Cron expression</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="0 0 * * 4" />
                    </FormControl>
                    <FormDescription>
                      Five unix fields: minute, hour, day of month, month, day of week.
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            ) : (
              <FormField
                control={form.control}
                name="time"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Time</FormLabel>
                    <FormControl>
                      <Input {...field} type="time" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            <p className="text-muted-foreground text-xs">
              {preview} ({buildCron(form.watch())})
            </p>
          </Section>

          <Section
            title="What it pulls"
            hint="The window is re-derived on every tick, so a lookback always ends at the tick itself. Two runs of this organization cannot share a window, or it would pull the same data twice.">
            <FormField
              control={form.control}
              name="windowMode"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Window</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="lookback">Last N months</SelectItem>
                      <SelectItem value="startDate">From a fixed date</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            {windowMode === 'lookback' ? (
              <FormField
                control={form.control}
                name="lookbackMonths"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Months</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        type="number"
                        min={PULL_LOOKBACK_MIN_MONTHS}
                        max={PULL_LOOKBACK_MAX_MONTHS}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            ) : (
              <FormField
                control={form.control}
                name="startDate"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>From</FormLabel>
                    <FormControl>
                      <Input {...field} type="date" />
                    </FormControl>
                    <FormDescription>
                      Every tick pulls from this date to now, so the window grows over time.
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}
          </Section>

          <Section title="State">
            <FormField
              control={form.control}
              name="enabled"
              render={({ field }) => (
                <FormItem className="flex items-center justify-between gap-4 space-y-0">
                  <div>
                    <FormLabel>Enabled</FormLabel>
                    <FormDescription>
                      Turning it off pauses the Scheduler job. The run and its job are kept, so it
                      can be resumed without re-entering any of this.
                    </FormDescription>
                  </div>
                  <FormControl>
                    <Switch checked={field.value} onCheckedChange={field.onChange} />
                  </FormControl>
                </FormItem>
              )}
            />
          </Section>

          {saveError && <p className="text-destructive text-sm">{saveError}</p>}
        </div>

        <div className="flex justify-end gap-2 border-t p-4">
          <Button type="button" variant="outline" onClick={onCancel}>
            Cancel
          </Button>
          <Button type="submit" disabled={saving}>
            {saving ? 'Saving...' : run ? 'Update run' : 'Add run'}
          </Button>
        </div>
      </form>
    </Form>
  );
}
